import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import NavBar from '../components/NavBar';

const STATUS_LABEL = {
  doing_well: { label: '🟢 Doing Well', color: '#16a34a' },
  slight_change: { label: '🟡 Slight Change', color: '#ca8a04' },
  significant_change: { label: '🟠 Significant Change', color: '#ea580c' },
  urgent: { label: '🔴 Urgent Concern', color: '#dc2626' },
};

const Row = ({ label, value }) => (
  (value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0)) ? null : (
    <div className="carelog-view-row">
      <span className="carelog-view-label">{label}</span>
      <span className="carelog-view-value">{Array.isArray(value) ? value.join(', ') : String(value)}</span>
    </div>
  )
);

const Section = ({ title, icon, children }) => (
  <div className="section-card" style={{ marginBottom: '1rem' }}>
    <h3 style={{ marginTop: 0, color: '#2c5364', fontSize: '1.05rem' }}>
      <i className={`fa-solid ${icon}`} style={{ marginRight: 8 }} /> {title}
    </h3>
    {children}
  </div>
);

const CareLogView = ({ user, onLogout }) => {
  const { clientId, logId } = useParams();
  const navigate = useNavigate();
  const [log, setLog] = useState(null);
  const [client, setClient] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [c, l] = await Promise.all([
          axios.get(`${API}/clients/${clientId}`),
          axios.get(`${API}/care-logs/${logId}`),
        ]);
        setClient(c.data);
        setLog(l.data);
      } catch { toast.error('Failed to load log'); }
    })();
  }, [clientId, logId]);

  const handlePrint = () => window.print();

  if (!log) return <div className="loading-screen">Loading…</div>;

  const status = STATUS_LABEL[log.overall_status];

  return (
    <div className="dashboard-container" data-testid="carelog-view">
      <NavBar onLogout={onLogout} />
      <div className="dashboard-content page-container carelog-print-area">
        <div className="carelog-view-toolbar">
          <button onClick={() => navigate(`/clients/${clientId}/care-logs`)} className="clog-back-btn">
            <i className="fa-solid fa-arrow-left" /> Back to logs
          </button>
          <button onClick={handlePrint} className="submit-button no-print" style={{ width: 'auto', padding: '0.5rem 1rem', fontSize: '0.85rem' }} data-testid="carelog-print-btn">
            <i className="fa-solid fa-print" /> Print / Save as PDF
          </button>
        </div>
        <div style={{ marginTop: '1rem', marginBottom: '1rem' }}>
          <h1 className="page-title">
            Care Log — {log.log_date}
          </h1>
          <p className="page-subtitle">
            <strong>{client?.name}</strong>
            {status && <> · <span style={{ color: status.color, fontWeight: 700 }}>{status.label}</span></>}
            {log.signed_by && <> · Signed by <strong>{log.signed_by}</strong></>}
          </p>
        </div>

        <Section title="Visit" icon="fa-clipboard-check">
          <Row label="Arrival" value={log.arrival_time} />
          <Row label="Departure" value={log.departure_time} />
          <Row label="Shift" value={log.shift_type} />
          <Row label="Location" value={log.location} />
        </Section>

        <Section title="Overall Status" icon="fa-heart-pulse">
          <Row label="Overall" value={status?.label} />
          <Row label="Baseline comparison" value={log.baseline_comparison} />
          <Row label="Appearance" value={log.appearance} />
          <Row label="Caregiver observation" value={log.caregiver_observation} />
        </Section>

        <Section title="Vitals" icon="fa-stethoscope">
          <Row label="Blood pressure" value={log.bp_systolic && log.bp_diastolic ? `${log.bp_systolic}/${log.bp_diastolic} mmHg (${log.bp_position || '—'})` : null} />
          <Row label="Pulse" value={log.pulse ? `${log.pulse} bpm ${log.pulse_rhythm || ''}` : null} />
          <Row label="Temperature" value={log.temperature ? `${log.temperature}°F (${log.temperature_method || '—'})` : null} />
          <Row label="Respiratory rate" value={log.respiratory_rate ? `${log.respiratory_rate} br/min` : null} />
          <Row label="Oxygen saturation" value={log.oxygen_saturation ? `${log.oxygen_saturation}%${log.on_oxygen ? ' (on O₂ ' + (log.oxygen_flow_rate || '') + ' L/min)' : ''}` : null} />
          <Row label="Weight" value={log.weight ? `${log.weight} lb` : null} />
          <Row label="Blood glucose" value={log.blood_glucose ? `${log.blood_glucose} mg/dL (${log.glucose_timing || '—'})` : null} />
        </Section>

        <Section title="Mood & Behavior" icon="fa-face-smile">
          <Row label="Mood" value={log.mood} />
          <Row label="Behaviors" value={log.behaviors} />
          <Row label="Notes" value={log.behavior_notes} />
        </Section>

        <Section title="Pain" icon="fa-hand-dots">
          <Row label="Pain present" value={log.pain_present ? 'Yes' : 'No'} />
          {log.pain_present && (
            <>
              <Row label="Level" value={`${log.pain_level}/10`} />
              <Row label="Location" value={log.pain_location} />
              <Row label="Description" value={log.pain_description} />
              <Row label="Vs. usual" value={log.pain_vs_usual} />
              <Row label="Action taken" value={log.pain_action_taken} />
            </>
          )}
        </Section>

        <Section title="Meals & Nutrition" icon="fa-utensils">
          <Row label="Breakfast" value={log.breakfast_amount} />
          <Row label="Lunch" value={log.lunch_amount} />
          <Row label="Dinner" value={log.dinner_amount} />
          <Row label="Snacks" value={log.snacks} />
          <Row label="Appetite" value={log.appetite} />
          <Row label="Concerns" value={log.eating_concerns} />
          <Row label="Notes" value={log.nutrition_notes} />
        </Section>

        <Section title="Fall / Incident" icon="fa-triangle-exclamation">
          <Row label="Fall today" value={log.fall_today ? 'YES' : 'No'} />
          <Row label="Near-fall" value={log.near_fall ? 'YES' : 'No'} />
          <Row label="Other incident" value={log.other_incident} />
          {log.fall_today && (
            <>
              <Row label="Time" value={log.fall_time} />
              <Row label="Location" value={log.fall_location} />
              <Row label="Witnessed" value={log.fall_witnessed ? 'Yes' : 'No'} />
              <Row label="Activity" value={log.fall_activity} />
              <Row label="Possible cause" value={log.fall_cause} />
              <Row label="Injury" value={log.fall_injury} />
              <Row label="Hit head" value={log.hit_head} />
              <Row label="Notifications" value={log.fall_notifications} />
              <Row label="Narrative" value={log.fall_narrative} />
            </>
          )}
        </Section>

        <Section title="Daily Summary" icon="fa-pen-to-square">
          <Row label="Summary" value={log.daily_summary} />
          <Row label="Concerns level" value={log.concerns_level} />
          <Row label="Concern notes" value={log.concerns_notes} />
          <Row label="Concern categories" value={log.concern_category} />
          <Row label="Notified" value={log.concerns_notified} />
        </Section>

        {(log.mental_status?.length || log.mental_change) && (
          <Section title="Mental & Cognitive" icon="fa-brain">
            <Row label="Status" value={log.mental_status} />
            <Row label="Change from normal" value={log.mental_change} />
            <Row label="Change notes" value={log.mental_change_notes} />
          </Section>
        )}
        {(log.fluid_intake_level || log.water_oz || log.other_fluids_oz) && (
          <Section title="Fluid Intake" icon="fa-glass-water">
            <Row label="Level" value={log.fluid_intake_level} />
            <Row label="Water" value={log.water_oz ? `${log.water_oz} oz` : null} />
            <Row label="Other fluids" value={log.other_fluids_oz ? `${log.other_fluids_oz} oz` : null} />
            <Row label="Signs" value={log.fluid_signs} />
          </Section>
        )}
        {(log.meds_handled || log.meds_status?.length) && (
          <Section title="Medication" icon="fa-pills">
            <Row label="Handled per care plan" value={log.meds_handled} />
            <Row label="Status" value={log.meds_status} />
            <Row label="Time" value={log.meds_time} />
            <Row label="Notifications" value={log.meds_notification} />
            <Row label="Notes" value={log.meds_notes} />
          </Section>
        )}
        {(log.mobility_level || log.mobility_vs_normal) && (
          <Section title="Mobility" icon="fa-person-walking">
            <Row label="Level" value={log.mobility_level} />
            <Row label="Vs. normal" value={log.mobility_vs_normal} />
            <Row label="Concerns" value={log.mobility_concerns} />
          </Section>
        )}
        {(log.adl_bathing || log.adl_dressing || log.adl_grooming || log.adl_oral_care || log.adl_toileting || log.adl_transfer || log.adl_meal) && (
          <Section title="ADLs" icon="fa-shower">
            <Row label="Bathing" value={log.adl_bathing} />
            <Row label="Dressing" value={log.adl_dressing} />
            <Row label="Grooming" value={log.adl_grooming} />
            <Row label="Oral care" value={log.adl_oral_care} />
            <Row label="Toileting" value={log.adl_toileting} />
            <Row label="Transfer" value={log.adl_transfer} />
            <Row label="Meal assistance" value={log.adl_meal} />
          </Section>
        )}
        {(log.urination_status?.length || log.bowel_movement) && (
          <Section title="Toileting & Elimination" icon="fa-toilet">
            <Row label="Urination" value={log.urination_status} />
            <Row label="Bowel movement" value={log.bowel_movement} />
            <Row label="Consistency" value={log.bowel_consistency} />
            <Row label="Bowel concerns" value={log.bowel_concerns} />
          </Section>
        )}
        {(log.skin_condition?.length || log.skin_notes) && (
          <Section title="Skin & Body" icon="fa-hand">
            <Row label="Condition" value={log.skin_condition} />
            <Row label="Location" value={log.skin_location} />
            <Row label="Is new?" value={log.skin_new} />
            <Row label="Notes" value={log.skin_notes} />
          </Section>
        )}
        {(log.sleep_quality || log.sleep_hours) && (
          <Section title="Sleep" icon="fa-moon">
            <Row label="Quality" value={log.sleep_quality} />
            <Row label="Hours" value={log.sleep_hours ? `${log.sleep_hours} hrs` : null} />
            <Row label="Issues" value={log.sleep_issues} />
          </Section>
        )}
        {(log.activities_completed?.length || log.engagement_level) && (
          <Section title="Activities & Social" icon="fa-person-hiking">
            <Row label="Activities" value={log.activities_completed} />
            <Row label="Engagement" value={log.engagement_level} />
          </Section>
        )}

        {log.signed_at && (
          <div className="clog-attest" style={{ marginTop: '1rem' }}>
            <i className="fa-solid fa-shield-halved" />
            <span>Signed by <strong>{log.signed_by}</strong> · {new Date(log.signed_at).toLocaleString()}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default CareLogView;
